// Loads everything the builder and the rule list need to name a device: the site's assets, its
// gateways' IO points, and current live values.
//
// All three come from endpoints that already exist in production, so this hook works today —
// only the rule persistence itself waits on the automation backend.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  loadAssets, loadIoPoints, loadLiveValues, indexAssets, indexIoPoints,
} from "../../lib/automationApi";
import {
  sourceKey, availableChannels, CHANNEL_LABELS, CONTROL_META_KEY,
} from "../../lib/automationModel";

export function useAutomationCatalogue(siteId) {
  const [assets, setAssets] = useState([]);
  const [ioPoints, setIoPoints] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) {
      setAssets([]); setIoPoints([]); setGateways([]); setLiveValues({});
      return;
    }
    setLoading(true);
    try {
      const [a, io, live] = await Promise.all([
        loadAssets(siteId),
        loadIoPoints(siteId),
        loadLiveValues(siteId),
      ]);
      setAssets(a);
      setIoPoints(io.ioPoints);
      setGateways(io.gateways);
      setLiveValues(live);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  // Refresh live values on the same 5 s cadence the control pages use, without re-fetching the
  // whole catalogue — a threshold being typed against a stale reading is worse than no reading.
  useEffect(() => {
    if (!siteId) return;
    const i = setInterval(async () => setLiveValues(await loadLiveValues(siteId)), 5000);
    return () => clearInterval(i);
  }, [siteId]);

  const assetsById = useMemo(() => indexAssets(assets), [assets]);
  const ioBySourceKey = useMemo(() => indexIoPoints(ioPoints), [ioPoints]);

  // One flat option list for every source picker. An asset appears once per channel it has
  // actually linked — a channel nobody wired is never offered.
  const sourceOptions = useMemo(() => {
    const out = [];

    for (const a of assets) {
      const io = ioBySourceKey[sourceKey({ kind: "io", gateway_id: a.gateway_id, io_point_name: a.io_point_name })];

      // The equipment's own control relay, offered as a write target under the asset's real
      // name. This is what lets a rule say "Start Hydrant Jockey Pump" instead of "Start Ground
      // Relay Board — Relay 1": the pump's primary link is its running-status DI, so without
      // this the asset could never be an output at all. Keeps the coil addressing in the label
      // so an engineer can still see exactly which relay they are about to drive.
      const controlPoint = (a.meta || {})[CONTROL_META_KEY];
      if (controlPoint) {
        const cio = ioBySourceKey[sourceKey({ kind: "io", gateway_id: a.gateway_id, io_point_name: controlPoint })];
        if (cio?.writable) {
          const source = { kind: "asset", asset_id: a.asset_id, channel: "control" };
          out.push({
            key: sourceKey(source),
            source,
            group: "asset",
            label: `${a.name} · control relay${cio.addressing ? ` — ${cio.addressing}` : ""}`,
            signal_type: cio.signal_type,
            unit: "",
            writable: true,
          });
        }
      }

      const channels = availableChannels(a);
      if (!channels.length) continue;                 // unlinked asset — nothing to watch yet
      for (const channel of channels) {
        const source = { kind: "asset", asset_id: a.asset_id, channel };
        out.push({
          key: sourceKey(source),
          source,
          group: "asset",
          label: channel === "primary" ? a.name : `${a.name} · ${CHANNEL_LABELS[channel] || channel}`,
          signal_type: channel === "primary" ? io?.signal_type || "" : "",
          unit: channel === "primary" ? io?.unit || "" : "",
          // Only the asset's primary link can be written, and only if that point is an output.
          writable: channel === "primary" && Boolean(io?.writable),
        });
      }
    }

    for (const p of ioPoints) {
      const source = { kind: "io", gateway_id: p.gateway_id, io_point_name: p.io_point_name };
      out.push({
        key: sourceKey(source),
        source,
        group: "io",
        label: `${p.label}${p.unit ? ` (${p.unit})` : ""}${p.addressing ? ` — ${p.addressing}` : ""}`,
        signal_type: p.signal_type,
        unit: p.unit,
        writable: p.writable,
      });
    }

    return out;
  }, [assets, ioPoints, ioBySourceKey]);

  const optionByKey = useMemo(() => {
    const out = {};
    for (const o of sourceOptions) out[o.key] = o;
    return out;
  }, [sourceOptions]);

  const ctx = useMemo(
    () => ({ assetsById, ioBySourceKey, sourceOptions, optionByKey }),
    [assetsById, ioBySourceKey, sourceOptions, optionByKey]
  );

  return { ctx, assets, ioPoints, gateways, liveValues, loading, reload: load };
}
